/**
 * Карточка бренда `/brands/:slug` (§7).
 *
 * Панель справа 520 px на ≥1440, 480 px на 1280–1439, отдельная страница ниже
 * 1280 и на телефоне. Таблица остаётся живой: маршрут вложен в `/brands` и
 * рендерится через `<Outlet/>`, затемнения фона нет, выезд 160 ms.
 *
 * Чтобы таблица **сжималась**, а не перекрывалась (§7), панель публикует свою
 * ширину в `--bm-card-width` и ставит на `<html>` атрибут `data-brand-card`:
 * таблица добавляет себе правый отступ по этой переменной. Пока таблицы нет,
 * панель просто лежит справа и ничего не ломает.
 *
 * Esc и клик вне закрывают карточку (слой регистрируется в overlay-stack,
 * иначе Esc закроет и поповер, и панель), фокус возвращается на тот элемент,
 * с которого карточку открыли — то есть на строку таблицы (§7, §13).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { Brand, ChannelCode } from '@shared/types.ts'
import {
  Button, Checkbox, ConsequenceLine, EmptyState, IconButton, IconChevronLeft,
  IconChevronRight, IconClose, IconComment, Modal, Skeleton,
} from '../../components/index.js'
import { isTopLayer, popLayer, pushLayer } from '../../components/overlay-stack.js'
import { channelCell } from '../../lib/draft-overlay.js'
import { useApp } from '../../store/app-store.js'
import { texts } from '../../texts.js'
import { BrandHeader } from './card/BrandHeader.js'
import { CategoriesBlock } from './card/CategoriesBlock.js'
import {
  CARD_BLOCKS, ChannelAbsent, ChannelBlock, CHANNEL_VALUE_FIELDS, channelsDiffer,
  fieldCell, missingForArea, type CardBlockDef,
} from './card/ChannelBlock.js'
import { CommentsBlock } from './card/CommentsBlock.js'
import { isEmptyValue, type CardBlockId } from './card/FieldsBlock.js'
import { HistoryBlock, fetchHistoryCount } from './card/HistoryBlock.js'
import { cardTexts } from './card/texts.card.js'
import './BrandCardScreen.css'

const BLOCKS_STORAGE_KEY = 'bm.card.blocks'

type BlockState = Partial<Record<CardBlockId, boolean>>

/** Свёрнутость блоков запоминается персонально (§7). */
function readBlockState(): BlockState {
  try {
    const raw = localStorage.getItem(BLOCKS_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const source = parsed as Record<string, unknown>
    const state: BlockState = {}
    for (const block of CARD_BLOCKS) {
      const value = source[block.id]
      if (typeof value === 'boolean') state[block.id] = value
    }
    return state
  } catch {
    return {}
  }
}

function writeBlockState(state: BlockState): void {
  try {
    localStorage.setItem(BLOCKS_STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* приватный режим: состояние живёт до конца сессии */
  }
}

function isChannelCode(value: string | null): value is ChannelCode {
  return value === 'retail' || value === 'b2b'
}

/** Ниже 1280 карточка — отдельная страница (§7, §4.8). */
function usePageMode(forced: boolean): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1279px)').matches,
  )

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1279px)')
    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches)
    media.addEventListener('change', onChange)
    setNarrow(media.matches)
    return () => media.removeEventListener('change', onChange)
  }, [])

  return forced || narrow
}

export function BrandCardScreen() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()

  const status = useApp((state) => state.status)
  const brands = useApp((state) => state.brands)
  const overlay = useApp((state) => state.overlay)
  const bootstrap = useApp((state) => state.bootstrap)
  const editChannel = useApp((state) => state.editChannel)
  const archiveBrand = useApp((state) => state.archiveBrand)

  const pageMode = usePageMode(search.get('card') === 'page')
  const panelRef = useRef<HTMLElement | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const layerRef = useRef<symbol | null>(null)

  const brand = useMemo(() => brands.find((item) => item.slug === slug) ?? null, [brands, slug])

  const urlChannel = search.get('ch')
  const defaultChannel = bootstrap?.settings.defaultChannel ?? 'retail'
  const [channel, setChannel] = useState<ChannelCode>(
    isChannelCode(urlChannel) ? urlChannel : defaultChannel,
  )
  useEffect(() => {
    if (isChannelCode(urlChannel)) setChannel(urlChannel)
  }, [urlChannel])

  /**
   * Канал карточки живёт в ссылке. Раньше вкладка переключалась только в
   * памяти: ссылкой на «Опт» было не поделиться, а после перезагрузки
   * карточка открывалась в рознице — хотя человек работал в опте.
   */
  const pickChannel = useCallback((next: ChannelCode) => {
    setChannel(next)
    const params = new URLSearchParams(search)
    params.set('ch', next)
    setSearch(params, { replace: true })
  }, [search, setSearch])

  const [blockState, setBlockState] = useState<BlockState>(readBlockState)
  const [tab, setTab] = useState<'comments' | 'history'>('comments')
  const [fieldFilter, setFieldFilter] = useState<{ code: string; label: string } | null>(null)
  const [commentsCount, setCommentsCount] = useState(brand?.commentsCount ?? 0)
  const [historyCount, setHistoryCount] = useState(0)
  const [archiving, setArchiving] = useState(false)
  const [copying, setCopying] = useState(false)
  /* На телефоне переписка открывается отдельным листом поверх карточки:
     рядом с параметрами ей не хватало высоты, лента не прокручивалась, и
     два блока мешали друг другу. Кнопка стоит над вкладками каналов —
     так видно, что разговор один на бренд, а не свой у розницы и опта. */
  const [talkOpen, setTalkOpen] = useState(false)

  /* Счётчик истории считаем сразу, не дожидаясь открытия вкладки: раньше на
     ней стоял ноль, пока по ней не нажмут, и ноль читался как «правок нет». */
  const brandId = brand?.id ?? null
  useEffect(() => {
    if (brandId === null) return
    let alive = true
    void fetchHistoryCount(brandId)
      .then((total) => { if (alive) setHistoryCount(total) })
      .catch(() => { /* вкладка покажет ошибку, когда её откроют */ })
    return () => { alive = false }
  }, [brandId])

  /** Куда вернуться по Esc и по кнопке «Закрыть»: таблица со своим состоянием. */
  const backTo = useMemo(() => {
    const params = new URLSearchParams(search)
    params.delete('card')
    const query = params.toString()
    return query === '' ? '/brands' : `/brands?${query}`
  }, [search])

  const close = useCallback(() => {
    navigate(backTo)
  }, [backTo, navigate])

  /* Фокус: запоминаем, откуда карточку открыли, и возвращаем туда же (§7). */
  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    return () => {
      const opener = openerRef.current
      if (opener && document.contains(opener)) opener.focus()
    }
  }, [])

  const prevNext = useMemo(() => {
    const index = brands.findIndex((item) => item.slug === slug)
    return {
      prev: index > 0 ? brands[index - 1] ?? null : null,
      next: index >= 0 && index < brands.length - 1 ? brands[index + 1] ?? null : null,
    }
  }, [brands, slug])

  const step = useCallback(
    (direction: 1 | -1) => {
      const target = direction === -1 ? prevNext.prev : prevNext.next
      if (!target) return
      const params = new URLSearchParams(search)
      const query = params.toString()
      navigate(query === '' ? `/brands/${target.slug}` : `/brands/${target.slug}?${query}`)
    },
    [navigate, prevNext, search],
  )

  /* Слой: Esc и клик вне адресованы только верхнему слою (overlay-stack). */
  useEffect(() => {
    const id = pushLayer('brand-card')
    layerRef.current = id
    return () => {
      popLayer(id)
      layerRef.current = null
    }
  }, [])

  useEffect(() => {
    function isTop(): boolean {
      const id = layerRef.current
      return id != null && isTopLayer(id)
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (!isTop()) return
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      /* ↑/↓ и J/K — предыдущий/следующий бренд без закрытия панели (§7).
         В поле ввода и внутри радиогруппы стрелки заняты своим делом. */
      const target = event.target
      const inControl =
        target instanceof HTMLElement &&
        target.closest('input, textarea, [contenteditable="true"], [role="radiogroup"], [role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]') !== null
      const isArrow = event.key === 'ArrowUp' || event.key === 'ArrowDown'
      const isLetter = event.key === 'j' || event.key === 'k' || event.key === 'о' || event.key === 'л'
      if (!isArrow && !isLetter) return
      if (inControl) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      event.preventDefault()
      const back = event.key === 'ArrowUp' || event.key === 'k' || event.key === 'л'
      step(back ? -1 : 1)
    }

    function onPointerDown(event: PointerEvent): void {
      if (!isTop() || pageMode) return
      const target = event.target
      if (!(target instanceof Node)) return
      if (panelRef.current?.contains(target)) return
      /* Клик по слою поверх панели (поповер в портале) панель не закрывает.
         Копилот — тоже не «вне карточки»: он живёт рядом с ней, и нажатие
         на шарик должно открывать разговор, а не закрывать бренд. */
      if (target instanceof HTMLElement && target.closest('.popover, .modal-overlay, .copilot-root')) return
      close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [close, pageMode, step])

  /* Ширина панели — наружу: таблица сжимается на эту величину, а не прячется. */
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-brand-card', pageMode ? 'page' : 'panel')
    return () => root.removeAttribute('data-brand-card')
  }, [pageMode])

  const withDock = overlay.count > 0

  if (status !== 'ready') {
    return (
      <aside className={panelClasses(pageMode, withDock)} aria-label={cardTexts.state.loadingLabel}>
        <Skeleton rows={8} columns={[160, 240]} label={cardTexts.state.loadingLabel} />
      </aside>
    )
  }

  if (!brand) {
    return (
      <aside className={panelClasses(pageMode, withDock)} aria-label={cardTexts.state.notFoundTitle}>
        <EmptyState
          title={cardTexts.state.notFoundTitle}
          text={cardTexts.state.notFoundText(slug)}
          actions={
            <Button variant="secondary" onClick={close}>
              {cardTexts.state.back}
            </Button>
          }
        />
      </aside>
    )
  }

  const user = bootstrap?.user ?? null
  const channels = bootstrap?.channels ?? []
  const channelName = channels.find((item) => item.code === channel)?.name ?? ''
  const otherChannel: ChannelCode = channel === 'retail' ? 'b2b' : 'retail'
  const otherName = channels.find((item) => item.code === otherChannel)?.name ?? ''
  const readonly = brand.status === 'archived'

  const presentCell = channelCell<boolean>(overlay, brand, channel, 'isPresent')
  const present = presentCell.value === true
  const missing = missingForArea(brand, channel, overlay, user?.area ?? null)
  const differs = channelsDiffer(brand, overlay, channel, otherChannel)
  const canEdit = Boolean(user?.permissions.includes('brands.edit'))

  function isBlockOpen(block: CardBlockDef): boolean {
    const stored = blockState[block.id]
    if (typeof stored === 'boolean') return stored
    /* По умолчанию развёрнуты блок зоны смотрящего и блок с его пропусками (§7). */
    const area = user?.area ?? null
    if (!area || area === 'admin') return block.id !== 'service'
    if ((block.areas as readonly string[]).includes(area)) return true
    return block.fields.some((field) => missing.includes(field))
  }

  function toggleBlock(block: CardBlockDef): void {
    const next: BlockState = { ...blockState, [block.id]: !isBlockOpen(block) }
    setBlockState(next)
    writeBlockState(next)
  }

  function filterByField(code: string, label: string): void {
    setFieldFilter({ code, label })
    setTab('history')
  }

  function goToMissing(): void {
    const first = missing[0]
    if (!first) return
    const block = CARD_BLOCKS.find((item) => item.fields.includes(first))
    if (block && !isBlockOpen(block)) {
      const next: BlockState = { ...blockState, [block.id]: true }
      setBlockState(next)
      writeBlockState(next)
    }
    window.setTimeout(() => {
      const row = panelRef.current?.querySelector(`[data-field="${first.code}"]`)
      const control = row?.querySelector<HTMLElement>('input, button, [role="radio"], [role="combobox"]')
      control?.focus()
      row?.scrollIntoView({ block: 'nearest' })
    }, 0)
  }

  /* «← Asko · Bosch →»: соседи по текущему списку (§4.10, §7). */
  const neighbours = [prevNext.prev?.name, prevNext.next?.name].filter(Boolean).join(' · ')

  return (
    <>
      {/* Лёгкое затенение под карточкой: она должна читаться как отдельный
          слой. Кликов не перехватывает — таблицей за ней пользуются не
          закрывая карточку. */}
      {pageMode ? null : <div className="card-scrim" aria-hidden="true" />}
    <aside
      ref={panelRef}
      className={panelClasses(pageMode, withDock)}
      role="dialog"
      aria-label={cardTexts.head.panelLabel(brand.name)}
    >
      <BrandHeader
        brand={brand}
        channel={channel}
        onChannel={pickChannel}
        differs={differs}
        missing={missing.length}
        onGoToMissing={goToMissing}
        onClose={close}
        pageHref={pageHref(slug, search)}
        onArchive={() => setArchiving(true)}
        onOpenHistory={() => setTab('history')}
        onFocusComment={() => setTab('comments')}
        talkSlot={(
          /* Видна только на телефоне (см. mobile.css). */
          <button
            type="button"
            className="card-panel__talk"
            aria-expanded={talkOpen}
            onClick={() => setTalkOpen(true)}
          >
            <IconComment size={16} />
            <span>{cardTexts.tabs.label}</span>
            <span className="card-panel__talk-count">{commentsCount}</span>
          </button>
        )}
      />

      <div className="card-panel__content">
      <div className="card-panel__body" id="card-channel-panel" role="tabpanel" aria-labelledby={`card-channel-${channel}`}>
        {present ? (
          CARD_BLOCKS.map((block) => (
            <ChannelBlock
              key={block.id}
              block={block}
              brand={brand}
              channel={channel}
              open={isBlockOpen(block)}
              onToggle={() => toggleBlock(block)}
              onFilterField={filterByField}
              readonly={readonly}
            >
              {block.hasCategories ? (
                <CategoriesBlock brand={brand} channel={channel} readonly={readonly} />
              ) : null}
            </ChannelBlock>
          ))
        ) : (
          <ChannelAbsent
            brandName={brand.name}
            channelName={channelName}
            otherChannelName={otherName}
            canEdit={canEdit && !readonly}
            onAdd={() => void editChannel(brand.id, channel, { isPresent: true })}
            onCopy={() => setCopying(true)}
          />
        )}

      </div>

      <div className="card-panel__side card-panel__foot" data-talk={talkOpen ? 'on' : 'off'}>
        {/* Отдельная плашка со скруглёнными краями и отступом от краёв панели:
            справа от значений должен стоять такой же блок, как слева, а не
            прижатая к стеклу колонка. */}
        <div className="card-panel__aside">
        {/* Заголовок листа на телефоне: он же объясняет, что разговор общий
            для бренда, а не для канала. */}
        <div className="card-panel__sheet-head">
          <span className="card-panel__sheet-title">{cardTexts.tabs.brandTalk(brand.name)}</span>
          <button
            type="button"
            className="card-panel__sheet-close"
            aria-label={texts.action.close}
            onClick={() => setTalkOpen(false)}
          >
            <IconClose size={20} />
          </button>
        </div>
        {/* Заголовок колонки: справа от значений стоит такой же блок, как
            слева, — и называться он должен так же внятно, как «Приоритет»
            или «Продвижение», а не начинаться сразу с переключателя. */}
        <h2 className="card-panel__aside-title t-h2">{cardTexts.tabs.asideTitle}</h2>
        <div className="card-panel__tabs" role="tablist" aria-label={cardTexts.tabs.label}>
          <button
            type="button"
            role="tab"
            id="card-tab-comments"
            aria-selected={tab === 'comments'}
            aria-controls="card-tab-panel"
            tabIndex={tab === 'comments' ? 0 : -1}
            className={tab === 'comments' ? 'card-panel__tab card-panel__tab--on' : 'card-panel__tab'}
            onClick={() => setTab('comments')}
          >
            {cardTexts.tabs.comments}
            <span className="card-panel__tab-count">{commentsCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="card-tab-history"
            aria-selected={tab === 'history'}
            aria-controls="card-tab-panel"
            tabIndex={tab === 'history' ? 0 : -1}
            className={tab === 'history' ? 'card-panel__tab card-panel__tab--on' : 'card-panel__tab'}
            onClick={() => setTab('history')}
          >
            {cardTexts.tabs.history}
            <span className="card-panel__tab-count">{historyCount}</span>
          </button>
        </div>

        <div
          className="card-panel__tab-body"
          id="card-tab-panel"
          role="tabpanel"
          aria-labelledby={tab === 'comments' ? 'card-tab-comments' : 'card-tab-history'}
        >
          {tab === 'comments' ? (
            <CommentsBlock brandId={brand.id} channel={channel} onCountChange={setCommentsCount} />
          ) : (
            <HistoryBlock
              brandId={brand.id}
              fieldFilter={fieldFilter}
              onClearFilter={() => setFieldFilter(null)}
              onCountChange={setHistoryCount}
            />
          )}
        </div>
        </div>
      </div>
      </div>

      <Modal
        open={archiving}
        onClose={() => setArchiving(false)}
        title={cardTexts.archive.title(brand.name)}
        size="s"
        dismissible={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setArchiving(false)}>
              {cardTexts.archive.cancel}
            </Button>
            <Button
              variant="danger-ghost"
              onClick={() => {
                setArchiving(false)
                void archiveBrand(brand.id)
              }}
            >
              {cardTexts.archive.confirm}
            </Button>
          </>
        }
      >
        {cardTexts.archive.text}
      </Modal>

      <CopyChannelDialog
        open={copying}
        brand={brand}
        from={otherChannel}
        to={channel}
        fromName={otherName}
        toName={channelName}
        onClose={() => setCopying(false)}
      />
    </aside>
    </>
  )
}

/**
 * Док черновика (§6.1) — отдельная полоса внизу оболочки. Панель на неё не
 * наезжает: пока черновик не пуст, она кончается на высоте дока.
 */
function panelClasses(pageMode: boolean, withDock: boolean): string {
  const classes = ['card-panel']
  if (pageMode) classes.push('card-panel--page')
  if (withDock) classes.push('card-panel--with-dock')
  return classes.join(' ')
}

function pageHref(slug: string, search: URLSearchParams): string {
  const params = new URLSearchParams(search)
  params.set('card', 'page')
  return `/brands/${slug}?${params.toString()}`
}

// ─────────────────── копирование значений между каналами (§7) ────────────────
interface CopyChannelDialogProps {
  open: boolean
  brand: Brand
  from: ChannelCode
  to: ChannelCode
  fromName: string
  toName: string
  onClose: () => void
}

/**
 * «Скопировать значения из Розницы»: список полей с чекбоксами — что именно
 * копировать — и одна операция в черновике (§7). Бренд при этом начинает
 * вестись в канале: без этого копировать было бы некуда.
 */
function CopyChannelDialog({ open, brand, from, to, fromName, toName, onClose }: CopyChannelDialogProps) {
  const overlay = useApp((state) => state.overlay)
  const editChannel = useApp((state) => state.editChannel)

  const source = useMemo(
    () =>
      CHANNEL_VALUE_FIELDS.filter((field) => !isEmptyValue(fieldCell(overlay, brand, from, field).value)),
    [brand, from, overlay],
  )
  const [picked, setPicked] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) setPicked(new Set(source.map((field) => field.code)))
  }, [open, source])

  function apply(): void {
    const patch: Record<string, unknown> = { isPresent: true }
    for (const field of source) {
      if (!picked.has(field.code)) continue
      patch[field.code] = fieldCell(overlay, brand, from, field).value
    }
    void editChannel(brand.id, to, patch)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cardTexts.channels.copyTitle(fromName, toName)}
      size="s"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {cardTexts.rename.cancel}
          </Button>
          <Button variant="primary" disabled={picked.size === 0} onClick={apply}>
            {cardTexts.channels.copyConfirm(picked.size)}
          </Button>
        </>
      }
    >
      {source.length === 0 ? (
        <ConsequenceLine>{cardTexts.channels.copyNothing}</ConsequenceLine>
      ) : (
        <>
          <p className="card-copy__hint t-meta">{cardTexts.channels.copyHint}</p>
          <div className="card-copy__list">
            {source.map((field) => (
              <Checkbox
                key={field.code}
                checked={picked.has(field.code)}
                label={field.label}
                onChange={(checked) => {
                  const next = new Set(picked)
                  if (checked) next.add(field.code)
                  else next.delete(field.code)
                  setPicked(next)
                }}
              />
            ))}
          </div>
        </>
      )}
    </Modal>
  )
}
